// src/features/stock/components/StockFilters.js
import React, { useState } from 'react';
import { Search, Filter, X, ChevronDown } from 'lucide-react';
import { STOCK_CATEGORIES, STOCK_STATUS } from '../constants/stockConstants';

const StockFilters = ({ filters, onFilterChange, onClearFilters }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState(filters.search || '');

  const handleSearch = (e) => {
    e.preventDefault();
    onFilterChange({ search: searchTerm });
  };

  const handleCategoryChange = (categoryId) => {
    const newCategories = filters.categories?.includes(categoryId)
      ? filters.categories.filter(id => id !== categoryId)
      : [...(filters.categories || []), categoryId];
    
    onFilterChange({ categories: newCategories });
  };

  const handleStatusChange = (status) => {
    const newStatuses = filters.status?.includes(status)
      ? filters.status.filter(s => s !== status)
      : [...(filters.status || []), status];
    
    onFilterChange({ status: newStatuses });
  };

  const hasActiveFilters = () => {
    return (
      filters.search ||
      filters.categories?.length ||
      filters.status?.length ||
      filters.low_stock ||
      filters.expiring_soon
    );
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by item name, SKU, or description..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Search
        </button>
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <Filter className="h-5 w-5" />
          Filters
          <ChevronDown className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
        {hasActiveFilters() && (
          <button
            type="button"
            onClick={onClearFilters}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
          >
            <X className="h-5 w-5" />
            Clear
          </button>
        )}
      </form>

      {/* Expanded Filters */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Categories */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Categories</h4>
              <div className="space-y-2">
                {STOCK_CATEGORIES.map(category => (
                  <label key={category.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.categories?.includes(category.id)}
                      onChange={() => handleCategoryChange(category.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">{category.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Status */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Status</h4>
              <div className="space-y-2">
                {Object.values(STOCK_STATUS).map(status => (
                  <label key={status.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={filters.status?.includes(status.id)}
                      onChange={() => handleStatusChange(status.id)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="ml-2 text-sm text-gray-600">{status.name}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Special Filters */}
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Special Filters</h4>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.low_stock}
                    onChange={(e) => onFilterChange({ low_stock: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Low Stock Items</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.expiring_soon}
                    onChange={(e) => onFilterChange({ expiring_soon: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Expiring Soon (30 days)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.out_of_stock}
                    onChange={(e) => onFilterChange({ out_of_stock: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-600">Out of Stock</span>
                </label>
              </div>
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-2">Date Range</h4>
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">From</label>
                <input
                  type="date"
                  value={filters.date_from || ''}
                  onChange={(e) => onFilterChange({ date_from: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">To</label>
                <input
                  type="date"
                  value={filters.date_to || ''}
                  onChange={(e) => onFilterChange({ date_to: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Filter Tags */}
      {hasActiveFilters() && (
        <div className="mt-4 flex flex-wrap gap-2">
          {filters.search && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800">
              Search: {filters.search}
              <button
                onClick={() => {
                  setSearchTerm('');
                  onFilterChange({ search: null });
                }}
                className="ml-2 hover:text-primary-600"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {filters.categories?.map(catId => {
            const category = STOCK_CATEGORIES.find(c => c.id === catId);
            return (
              <span key={catId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                {category?.name}
                <button
                  onClick={() => handleCategoryChange(catId)}
                  className="ml-2 hover:text-blue-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
          {filters.status?.map(statusId => (
            <span key={statusId} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
              {STOCK_STATUS[statusId.toUpperCase()]?.name}
              <button
                onClick={() => handleStatusChange(statusId)}
                className="ml-2 hover:text-green-600"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {filters.low_stock && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
              Low Stock
              <button
                onClick={() => onFilterChange({ low_stock: false })}
                className="ml-2 hover:text-yellow-600"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default StockFilters;